package one.healthium.insights.data.mapper

import one.healthium.common.mapper.ColorMapper
import one.healthium.insights.data.dto.ContentEntryDto
import one.healthium.insights.data.dto.HintDto
import one.healthium.insights.data.dto.TextStyleDto
import one.healthium.insights.domain.models.CardModel
import one.healthium.insights.domain.models.ColorModel
import one.healthium.insights.domain.models.ContentEntry
import one.healthium.insights.domain.models.ContentEntry.Anchor
import one.healthium.insights.domain.models.ContentEntry.Button
import one.healthium.insights.domain.models.ContentEntry.CardEntry
import one.healthium.insights.domain.models.ContentEntry.Carousel
import one.healthium.insights.domain.models.ContentEntry.Column
import one.healthium.insights.domain.models.ContentEntry.DayEntry
import one.healthium.insights.domain.models.ContentEntry.Decorator
import one.healthium.insights.domain.models.ContentEntry.GroupEntry
import one.healthium.insights.domain.models.ContentEntry.HeaderEntry
import one.healthium.insights.domain.models.ContentEntry.HorizontalList
import one.healthium.insights.domain.models.ContentEntry.ImageCard
import one.healthium.insights.domain.models.ContentEntry.ImageEntry
import one.healthium.insights.domain.models.ContentEntry.Line
import one.healthium.insights.domain.models.ContentEntry.ListItem
import one.healthium.insights.domain.models.ContentEntry.ListItemPreview
import one.healthium.insights.domain.models.ContentEntry.PlainText
import one.healthium.insights.domain.models.ContentEntry.RichTextEntry
import one.healthium.insights.domain.models.ContentEntry.Row
import one.healthium.insights.domain.models.ContentEntry.ShoppingList
import one.healthium.insights.domain.models.ContentEntry.Space
import one.healthium.insights.domain.models.ContentEntry.StickyContent
import one.healthium.insights.domain.models.ContentEntry.Tips
import one.healthium.insights.domain.models.ContentEntry.TitleEntry
import one.healthium.insights.domain.models.HintModel
import one.healthium.insights.domain.models.MealDetailsModel
import one.healthium.insights.domain.models.MealModel
import one.healthium.insights.domain.models.TextStyleModel
import one.healthium.insights.domain.models.ThemeColor
import one.healthium.insights.domain.models.TipModel
import one.healthium.insights.domain.models.TipsConfigModel

internal class ContentMapper(
    private val actionMapper: ActionMapper,
    private val colorMapper: ColorMapper,
    private val mealNutritionMapper: MealNutritionMapper,
) {
    fun mapContentEntry(dto: ContentEntryDto): ContentEntry? {
        return when (dto) {
            is ContentEntryDto.Image -> ImageEntry(
                url = dto.url,
                thumbUrl = dto.thumbUrl,
                height = dto.height,
            )

            is ContentEntryDto.Text -> ContentEntry.TextEntry(text = dto.text)
            is ContentEntryDto.RichText -> RichTextEntry(text = dto.text, insets = dto.insets)
            is ContentEntryDto.Title -> TitleEntry(text = dto.text)
            is ContentEntryDto.Day -> DayEntry(
                title = dto.title,
                meals = dto.meals?.map { mealDto ->
                    MealModel(
                        title = mealDto.title,
                        description = mealDto.description,
                        details = mealDto.details?.map { mealDetailsDto ->
                            MealDetailsModel(
                                title = mealDetailsDto.title,
                                text = mealDetailsDto.text,
                            )
                        }.orEmpty(),
                        nutrition = mealDto.nutrition?.let(mealNutritionMapper::dtoToModel)
                    )
                }.orEmpty()
            )

            is ContentEntryDto.Card -> {
                val action = actionMapper.dtoToModel(dto.action) ?: return null
                CardEntry(
                    title = dto.title,
                    imageUrl = dto.image,
                    hint = dto.hint?.let { hintDto ->
                        HintModel(
                            text = hintDto.text,
                            iconName = hintDto.icon,
                        )
                    },
                    action = action
                )
            }

            is ContentEntryDto.Group -> GroupEntry(
                backgroundColor = dto.backgroundColor?.toColor(),
                insets = dto.insets,
                cornerRadius = dto.cornerRadius,
                content = dto.content.mapNotNull(this::mapContentEntry),
            )

            is ContentEntryDto.Carousel -> Carousel(
                variant = dto.variant,
                content = dto.content.mapNotNull {
                    actionMapper.dtoToModel(it.action)?.let { action ->
                        CardModel(
                            title = it.title,
                            thumbUrl = it.thumbUrl,
                            action = action
                        )
                    }
                },
            )

            is ContentEntryDto.Header -> HeaderEntry(text = dto.text)

            is ContentEntryDto.ImageCard -> actionMapper.dtoToModel(dto.action)?.let {
                ImageCard(
                    title = dto.title,
                    hint = dto.hint?.let(::mapHint),
                    imageUrl = dto.image,
                    action = it,
                )
            }

            is ContentEntryDto.ListItem -> actionMapper.dtoToModel(dto.action)?.let {
                ListItem(
                    title = dto.title,
                    emoji = dto.emoji,
                    backgroundColor = dto.backgroundColor?.toColor(),
                    action = it,
                )
            }

            is ContentEntryDto.ListItemPreview -> actionMapper.dtoToModel(dto.action)?.let {
                ListItemPreview(
                    title = dto.title,
                    description = dto.description,
                    thumbUrl = dto.thumbUrl,
                    action = it,
                )
            }

            is ContentEntryDto.Tips -> Tips(
                config = dto.config?.let {
                    TipsConfigModel(
                        backgroundColor = dto.config.backgroundColor?.let(colorMapper::parseColor),
                        textColor = dto.config.textColor?.let(colorMapper::parseColor),
                    )
                },
                tips = dto.tips.map { tipDto ->
                    TipModel(
                        id = tipDto.id,
                        title = tipDto.title,
                        text = tipDto.text,
                    )
                }
            )

            is ContentEntryDto.ShoppingList -> actionMapper.dtoToModel(dto.action)?.let {
                ShoppingList(
                    name = dto.name,
                    color = dto.color?.toColor(),
                    action = it,
                )
            }

            is ContentEntryDto.PlainText -> PlainText(
                text = dto.text,
                style = dto.style?.toTextStyle(),
                insets = dto.insets,
            )

            is ContentEntryDto.Line -> Line(
                thickness = dto.thickness,
                color = dto.color?.toColor(),
                indent = dto.indent,
                endIndent = dto.endIndent,
            )

            is ContentEntryDto.Button -> actionMapper.dtoToModel(dto.action)?.let {
                Button(
                    text = dto.text,
                    height = dto.height,
                    style = dto.style?.toTextStyle(),
                    containerColor = dto.containerColor?.toColor(),
                    action = it,
                    insets = dto.insets,
                )
            }

            is ContentEntryDto.Row -> Row(
                weights = dto.weights,
                content = dto.content.mapNotNull(this::mapContentEntry),
                insets = dto.insets,
                action = dto.action?.let(actionMapper::dtoToModel),
            )

            is ContentEntryDto.Column -> Column(
                content = dto.content.mapNotNull(this::mapContentEntry),
                insets = dto.insets,
                action = dto.action?.let(actionMapper::dtoToModel),
            )

            is ContentEntryDto.Space -> Space(size = dto.size)

            is ContentEntryDto.Anchor -> Anchor(
                content = mapContentEntry(dto.content) ?: return null,
                anchorId = dto.anchorId,
            )

            is ContentEntryDto.Decorator -> Decorator(
                content = mapContentEntry(dto.content) ?: return null,
                backgroundColor = dto.backgroundColor.toColor(),
                insets = dto.insets,
                cornerRadius = dto.cornerRadius,
                margins = dto.margins,
                action = dto.action?.let(actionMapper::dtoToModel),
            )

            is ContentEntryDto.Supplement -> null
            is ContentEntryDto.Default -> null
            is ContentEntryDto.HorizontalList -> HorizontalList(
                content = dto.content.mapNotNull(this::mapContentEntry),
                contentInsets = dto.contentInsets,
            )

            is ContentEntryDto.StickyContent -> StickyContent(
                content = mapContentEntry(dto.content) ?: return null,
            )

            is ContentEntryDto.UnitSelectorContainer -> ContentEntry.UnitSelectorContainer(
                metric = dto.metric.mapNotNull(this::mapContentEntry),
                imperial = dto.imperial.mapNotNull(this::mapContentEntry),
            )
        }
    }

    private fun mapHint(dto: HintDto): HintModel {
        return HintModel(
            text = dto.text,
            iconName = dto.icon,
        )
    }

    private fun String.toColor(): ColorModel? {
        return when (this.lowercase()) {
            "primary" -> ColorModel.Theme(ThemeColor.Primary)
            "secondary" -> ColorModel.Theme(ThemeColor.Secondary)
            else -> colorMapper.parseColor(this)?.let { ColorModel.Hex(it) }
        }
    }

    private fun TextStyleDto.toTextStyle(): TextStyleModel {
        return TextStyleModel(
            fontSize = fontSize,
            fontWeight = fontWeight,
            fontStyle = fontStyle,
            color = color?.toColor(),
            alignment = alignment,
        )
    }
}